// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode'; // Use a library to decode JWTs safely

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // Store user info (like username) if needed
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true); // Prevent flicker on initial load

    useEffect(() => {
        // Check for token on initial load
        const token = localStorage.getItem('authToken');
        if (token) {
            try {
                // Decode token to check expiry and get user info
                // NOTE: Decoding checks format and expiry, but doesn't verify signature!
                // Signature verification should happen on the backend.
                const decodedToken = jwtDecode(token);
                const currentTime = Date.now() / 1000;

                if (decodedToken.exp > currentTime) {
                    // Token is valid and not expired
                    setUser({ username: decodedToken.username /* add other fields if present */ });
                    setIsLoggedIn(true);
                } else {
                    // Token expired
                    localStorage.removeItem('authToken');
                    setIsLoggedIn(false);
                    setUser(null);
                }
            } catch (error) {
                // Invalid token format
                console.error("Error decoding token on load:", error);
                localStorage.removeItem('authToken');
                setIsLoggedIn(false);
                setUser(null);
            }
        } else {
          setIsLoggedIn(false);
          setUser(null);
        }
        setLoading(false); // Done checking
    }, []);

    const login = (token) => {
        try {
          const decodedToken = jwtDecode(token);
          localStorage.setItem('authToken', token);
          setUser({
            // Make sure these property names match exactly what's IN the token payload
            userId: decodedToken.userId,
            username: decodedToken.username,
            is_admin: decodedToken.is_admin // <<< ADD is_admin HERE!
          });
          setIsLoggedIn(true);
        } catch(error) {
          console.error("Error decoding token on login:", error);
          // Handle invalid token during login if necessary
        }
    };

    const logout = () => {
        localStorage.removeItem('authToken');
        setUser(null);
        setIsLoggedIn(false);
        // Optionally redirect to home or login page using useNavigate() if needed here
    };

    // Don't render children until loading state is determined
    if (loading) {
        return null; // Or a loading spinner
    }

    return (
        <AuthContext.Provider value={{ isLoggedIn, user, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the auth context easily
export const useAuth = () => {
    return useContext(AuthContext);
};